import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Icon } from '@repo/ui';
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  Label,
  Select,
} from '@repo/ui';
import { PageLayout } from '@xtnd/web/shared/layout';
import { webhookEventsApi, WebhookEventQuery } from '../api/webhookEventsApi';

export function WebhookActivityPage() {
  // Filter state
  const [selectedModel, setSelectedModel] = useState('');

  // Pagination state
  const [nextToken, setNextToken] = useState<string | undefined>();
  const pageSize = 50;

  // Build query parameters for API - only model filter and pagination
  const queryParams: WebhookEventQuery = {
    limit: pageSize,
    nextToken,
    model: selectedModel || undefined,
  };

  // Fetch webhook events
  const {
    data: webhookEventsResponse,
    isLoading: isLoadingEvents,
    error: eventsError,
    refetch: refetchEvents,
  } = useQuery({
    queryKey: ['webhook-events', selectedModel, nextToken],
    queryFn: () => webhookEventsApi.queryWebhookEvents(queryParams),
    staleTime: 0, // Always fetch fresh data
    gcTime: 0, // Don't cache
    refetchOnWindowFocus: false,
  });

  // Fetch webhook event statistics (last 24 hours)
  const {
    data: webhookStats,
    isLoading: isLoadingStats,
  } = useQuery({
    queryKey: ['webhook-events-stats'],
    queryFn: () => webhookEventsApi.getWebhookEventStats(),
    staleTime: 30 * 1000, // 30 seconds
    refetchOnWindowFocus: false,
  });

  // Fetch model types for filter
  const { data: modelTypes } = useQuery({
    queryKey: ['webhook-model-types'],
    queryFn: () => webhookEventsApi.getModelTypes(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });

  // Extract events from response
  const events = webhookEventsResponse?.events || [];
  const hasMore = webhookEventsResponse?.hasMore || false;

  // Reset pagination when filter changes
  useEffect(() => {
    setNextToken(undefined);
  }, [selectedModel]);

  const handleNextPage = () => {
    if (hasMore && webhookEventsResponse?.nextToken) {
      setNextToken(webhookEventsResponse.nextToken);
    }
  };

  const handlePreviousPage = () => {
    setNextToken(undefined);
  };

  const handleModelChange = (value: string) => {
    setSelectedModel(value);
    // nextToken will be reset by useEffect
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'processed':
        return <Icon name="HiCheckCircle" className="h-4 w-4 text-green-500" />;
      case 'processing':
        return <Icon name="HiClock" className="h-4 w-4 text-yellow-500" />;
      case 'failed':
        return <Icon name="HiXCircle" className="h-4 w-4 text-red-500" />;
      case 'received':
        return <Icon name="HiClock" className="h-4 w-4 text-blue-500" />;
      default:
        return <Icon name="HiClock" className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string): string => {
    const baseClass = 'inline-flex px-2 py-1 text-xs font-semibold rounded-full';

    switch (status.toLowerCase()) {
      case 'processed':
        return `${baseClass} bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200`;
      case 'processing':
        return `${baseClass} bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200`;
      case 'failed':
        return `${baseClass} bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200`;
      case 'received':
        return `${baseClass} bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200`;
      default:
        return `${baseClass} bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200`;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getClioUrl = (model: string, objectId: string): string | null => {
    const m = model?.toLowerCase();
    switch (m) {
      case 'bill':
        return `https://app.clio.com/nc/#/bills/${objectId}`;
      case 'matter':
        return `https://app.clio.com/nc/#/matters/${objectId}`;
      case 'contact':
        return `https://app.clio.com/nc/#/contacts/${objectId}`;
      case 'activity':
        return `https://app.clio.com/nc/#/activities`;
      case 'task':
      case 'tasks':
        return `https://app.clio.com/nc/#/tasks`;
      default:
        return null;
    }
  };

  // Retry actions removed for now

  if (isLoadingEvents && !nextToken) {
    return (
      <PageLayout
        title="Clio Webhook Activity"
        description="Monitor and manage Clio webhook events and processing status"
        isLoading={true}
        skeletonProps={{
          summaryCardCount: 0,
          tableRows: 0,
          tableColumns: 0,
          showTable: false,
          showSummaryCards: false,
        }}
      >
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-xtnd-blue mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading webhook events...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (eventsError) {
    return (
      <PageLayout
        title="Clio Webhook Activity"
        description="Monitor and manage Clio webhook events and processing status"
      >
        <div className="flex flex-col items-center justify-center py-12">
          <div className="text-center">
            <Icon name="HiExclamation" className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Failed to Load Webhook Events
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {eventsError instanceof Error ? eventsError.message : 'An unknown error occurred'}
            </p>
            <Button
              onClick={() => refetchEvents()}
              variant="primary"
              leftIcon={<Icon name="HiRefresh" className="h-4 w-4" />}
            >
              Retry
            </Button>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title="Clio Webhook Activity"
      description="Monitor and manage Clio webhook events and processing status"
      headerContent={
        <Button
          onClick={() => refetchEvents()}
          variant="outline"
          leftIcon={<Icon name="HiRefresh" className="h-4 w-4" />}
        >
          Refresh
        </Button>
      }
    >
      <div className="space-y-6">

      {/* Statistics Cards - Last 24 Hours */}
      {webhookStats && !isLoadingStats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card variant="default">
            <CardBody>
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Icon name="HiDocumentText" className="h-8 w-8 text-xtnd-blue" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Events</p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {webhookStats.totalEvents.toLocaleString()}
                  </p>
                </div>
              </div>
            </CardBody>
          </Card>

          <Card variant="default">
            <CardBody>
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Icon name="HiCheckCircle" className="h-8 w-8 text-green-500" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Processed</p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {webhookStats.byStatus.processed.toLocaleString()}
                  </p>
                </div>
              </div>
            </CardBody>
          </Card>

          <Card variant="default">
            <CardBody>
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Icon name="HiXCircle" className="h-8 w-8 text-red-500" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Failed</p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {webhookStats.byStatus.failed.toLocaleString()}
                  </p>
                </div>
              </div>
            </CardBody>
          </Card>

          <Card variant="default">
            <CardBody>
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Icon name="HiClock" className="h-8 w-8 text-yellow-500" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Last 24h</p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {webhookStats.recentActivity.last24Hours.toLocaleString()}
                  </p>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
      )}

      {/* Model Filter */}
      <Card variant="default">
        <CardBody>
          <div className="flex items-center gap-4">
            <div className="w-64">
              <Label htmlFor="model-filter">Filter by Model</Label>
              <Select
                options={[
                  { value: '', label: 'All Models' },
                  ...(modelTypes?.map(model => ({ value: model, label: model })) || [])
                ]}
                value={selectedModel}
                onChange={(value) => handleModelChange(Array.isArray(value) ? (value[0] ?? '') : value)}
              />
            </div>
            {selectedModel && (
              <Button
                onClick={() => handleModelChange('')}
                variant="outline"
                size="sm"
              >
                Clear Filter
              </Button>
            )}
          </div>
        </CardBody>
      </Card>

      {/* Events Table */}
      <Card variant="default">
        <CardHeader>
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-xtnd-dark dark:text-xtnd-white">
              Webhook Events {selectedModel && `(${selectedModel})`}
            </h2>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Showing {events.length} events {nextToken ? '(Page 2+)' : '(Page 1)'}
            </div>
          </div>
        </CardHeader>
        <CardBody>
          {events.length === 0 ? (
            <div className="text-center py-8">
              <Icon name="HiDocumentText" className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No Webhook Events Found
              </h3>
              <p className="text-gray-500 dark:text-gray-400">
                {selectedModel
                  ? `No events found for model "${selectedModel}".`
                  : 'No webhook events have been received yet.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Received At
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Model
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Event
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Object ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Retry Count
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                  {events.map((event) => (
                    <tr key={event.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {formatDate(event.receivedAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex px-2 py-1 text-xs font-medium rounded-md bg-xtnd-light text-xtnd-dark">
                          {event.model}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {event.eventType}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {(() => {
                          const url = getClioUrl(event.model, event.objectId);
                          return url ? (
                            <a
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xtnd-blue underline"
                            >
                              {event.objectId}
                            </a>
                          ) : (
                            event.objectId
                          );
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          {getStatusIcon(event.status)}
                          <span className={getStatusColor(event.status)}>
                            {event.status}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {event.retryCount}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Pagination */}
      {(events.length > 0 || nextToken) && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-700 dark:text-gray-300">
            {nextToken ? 'Page 2+' : 'Page 1'} • Showing {events.length} of {pageSize} per page
          </div>
          <div className="flex space-x-2">
            <Button
              onClick={handlePreviousPage}
              disabled={!nextToken}
              variant="outline"
              leftIcon={<Icon name="HiChevronLeft" className="h-4 w-4" />}
            >
              First Page
            </Button>
            <Button
              onClick={handleNextPage}
              disabled={!hasMore || isLoadingEvents}
              variant="outline"
              rightIcon={<Icon name="HiChevronRight" className="h-4 w-4" />}
            >
              {isLoadingEvents ? 'Loading...' : 'Next Page'}
            </Button>
          </div>
        </div>
      )}
      </div>
    </PageLayout>
  );
}
